import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { PlaylistItem, FetchPlaylistResponse } from '../../../swagger'

/**
 * Handles drag-and-drop logic to reorder playlist items.
 *
 * This function is triggered when a drag-and-drop action finishes.
 * It checks if the dragged item (active) has been dropped over a different item (over).
 * If yes, it finds the original and new indices of the dragged item,
 * rearranges the playlist items array accordingly,
 * updates each item's position to maintain a 1-based order,
 * and finally updates the component state with the new ordered list.
 *
 *
 * @param event - Drag event from DnD Kit
 * @param playlist - The current playlist state
 * @param setPlaylist - Setter function to update the playlist state
 */
export const handleDragEnd = (
  event: DragEndEvent,
  playlist: FetchPlaylistResponse,
  setPlaylist: (updated: FetchPlaylistResponse) => void
): void => {
  // Get the current list of playlist items or empty array if undefined
  const items = playlist?.playlistItems ?? []

  // Destructure active (dragged) and over (drop target) elements from the event
  const { active, over } = event

  // Proceed only if the dragged item is dropped over a different item
  if (active.id !== over?.id) {
    // Find the original index of the dragged item in the list
    const oldIndex = items.findIndex(
      (item) => item.playlistItemKey === active.id
    )

    // Find the index where the dragged item is dropped
    const newIndex = items.findIndex(
      (item) => item.playlistItemKey === over?.id
    )

    // Create a new array with items reordered by moving the dragged item from oldIndex to newIndex
    const newItems = arrayMove(items, oldIndex, newIndex)

    // Update each item's position property to reflect the new order (1-based indexing)
    const updatedItems: PlaylistItem[] = newItems.map((item, index) => ({
      ...item,
      position: index + 1,
    }))

    // Update the component state with the newly ordered playlist and items
    setPlaylist({
      ...playlist,
      playlist: playlist?.playlist,
      playlistItems: updatedItems,
    })
  }
}
